import React, { useMemo } from 'react';
import {
    GoogleMap, InfoWindow, Marker, MarkerClusterer
} from '@react-google-maps/api';
import { Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MapArrow from '../../Map/MapArrow';
import { PublishedPublicOrderType, TenantForMapType } from '../../../types';

type ClustererInstance = Parameters<NonNullable<React.ComponentProps<typeof MarkerClusterer>['onLoad']>>[0];

const center = { lat: 63, lng: 18 };

const options = {
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    clickableIcons: false,
    zoom: 5,
    minZoom: 4,
    maxZoom: 20
};

type PropsType = {
    jobs: PublishedPublicOrderType[] | null;
    tenants: TenantForMapType[];
    height?: number | string;
};

export default function MarkerMap({
    jobs,
    tenants,
    height = '100%'
}: PropsType) {
    const navigate = useNavigate();
    const [selectedTenantId, setSelectedTenantId] = React.useState<number | null>(null);
    const [showPickupInfoWindow, setShowPickupInfoWindow] = React.useState<number[]>([]);
    const [showDeliveryInfoWindow, setShowDeliveryInfoWindow] = React.useState<number[]>([]);
    const [tenantClusterer, setTenantClusterer] = React.useState<ClustererInstance | null>(null);

    const goToTenant = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const id = event.currentTarget.id.replace('tenantId-', '');
        navigate(`/tenants/${id}`);
    }, [navigate]);

    function getStatusText(tenant: TenantForMapType): string {
        if (tenant.isAccepted) {
            return 'Accepterad';
        }
        if (tenant.isRejected) {
            return 'Avvisad';
        }
        if (tenant.regDate === null) {
            return 'Ej registrerad';
        }
        return 'Väntande';
    }
    const renderInfoContent = React.useCallback((tenant: TenantForMapType) => (
        <div>
            <Typography variant="h6">{tenant.name}</Typography>
            <Typography variant="body2">
                {`Org Number: ${tenant.orgNumber}`}
            </Typography>
            <Typography variant="body2">
                {`Users: ${tenant.userCount}`}
            </Typography>
            <Typography variant="body2">
                {`Registered: ${tenant.regDate ? new Date(tenant.regDate).toISOString().slice(0, 10) : 'N/A'}`}
            </Typography>
            <Typography variant="body2">
                {`Status: ${getStatusText(tenant)}`}
            </Typography>
            <Button
                id={`tenantId-${tenant.id}`}
                variant="contained"
                onClick={goToTenant}
                sx={{ mt: 1 }}
                fullWidth
            >
                Gå till företag
            </Button>
        </div>
    ), [goToTenant]);

    const toggleShowInfoWindow = React.useCallback((index: number) => {
        setSelectedTenantId((prevId) => (prevId === index ? null : index));
    }, []);

    const selectedTenant = useMemo(
        () => tenants.find((t) => t.id === selectedTenantId) ?? null,
        [tenants, selectedTenantId]
    );

    React.useEffect(() => {
        tenantClusterer?.repaint();
    }, [tenants, tenantClusterer]);

    const toggleShowPickupInfoWindow = (index: number) => {
        if (showPickupInfoWindow.includes(index)) {
            setShowPickupInfoWindow((prevItems) => prevItems.filter((item) => item !== index));
        } else {
            setShowPickupInfoWindow((prevItems) => [...prevItems, index]);
        }
    };

    const toggleShowDeliveryInfoWindow = (index: number) => {
        if (showDeliveryInfoWindow.includes(index)) {
            setShowDeliveryInfoWindow((prevItems) => prevItems.filter((item) => item !== index));
        } else {
            setShowDeliveryInfoWindow((prevItems) => [...prevItems, index]);
        }
    };

    return (
        <GoogleMap
            mapContainerStyle={{ height, overflow: 'visible' }}
            center={center}
            options={options}
        >
            {jobs && (
                jobs.map((job) => {
                    return (
                        <React.Fragment key={job.id}>
                            <Marker
                                position={{ lat: job.deliveryLat, lng: job.deliveryLng }}
                                icon="/marker-dot-teal.png"
                                onClick={() => toggleShowDeliveryInfoWindow(job.id)}
                            >
                                {showDeliveryInfoWindow.includes(job.id) && (
                                    <InfoWindow
                                        position={{ lat: job.deliveryLat, lng: job.deliveryLng }}
                                        onCloseClick={() => toggleShowDeliveryInfoWindow(job.id)}
                                    >
                                        <div>
                                            <Typography variant="h6">
                                                {job.pickupCity}
                                                -
                                                {job.deliveryCity}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`företag: ${job.tenant}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Hämtas: ${job.lastPickupDate ? new Date(job.lastPickupDate).toISOString().slice(0, 10) : 'N/A'}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Lämnas: ${job.lastDeliveryDate ? new Date(job.lastDeliveryDate).toISOString().slice(0, 10) : 'N/A'}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Flakmeter: ${job.loadingMeters ? job.loadingMeters : ''}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Vikt: ${job.grossWeight ? job.grossWeight : ''}`}
                                            </Typography>
                                            <Button
                                                id={`orderId-${job.id}`}
                                                variant="contained"
                                                onClick={() => navigate(`/orders/${job.id}`)}
                                                sx={{ mt: 1 }}
                                                fullWidth
                                            >
                                                Gå till uppdrag
                                            </Button>
                                        </div>
                                    </InfoWindow>
                                )}
                            </Marker>
                            <Marker
                                position={{ lat: job.pickupLat, lng: job.pickupLng }}
                                icon="/marker-dot-teal.png"
                                onClick={() => toggleShowPickupInfoWindow(job.id)}
                            >
                                {showPickupInfoWindow.includes(job.id) && (
                                    <InfoWindow
                                        position={{ lat: job.pickupLat, lng: job.pickupLng }}
                                        onCloseClick={() => toggleShowPickupInfoWindow(job.id)}
                                    >
                                        <div>
                                            <Typography variant="h6">
                                                {job.pickupCity}
                                                -
                                                {job.deliveryCity}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`företag: ${job.tenant}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Hämtas: ${job.lastPickupDate ? new Date(job.lastPickupDate).toISOString().slice(0, 10) : 'N/A'}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Lämnas: ${job.lastDeliveryDate ? new Date(job.lastDeliveryDate).toISOString().slice(0, 10) : 'N/A'}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Flakmeter: ${job.loadingMeters ? job.loadingMeters : ''}`}
                                            </Typography>
                                            <Typography variant="body2">
                                                {`Vikt: ${job.grossWeight ? job.grossWeight : ''}`}
                                            </Typography>
                                            <Button
                                                id={`orderId-${job.id}`}
                                                variant="contained"
                                                onClick={() => navigate(`/orders/${job.id}`)}
                                                sx={{ mt: 1 }}
                                                fullWidth
                                            >
                                                Gå till uppdrag
                                            </Button>
                                        </div>
                                    </InfoWindow>
                                )}
                            </Marker>
                            <MapArrow
                                pickupLat={job.pickupLat}
                                pickupLng={job.pickupLng}
                                deliveryLat={job.deliveryLat}
                                deliveryLng={job.deliveryLng}
                            />
                        </React.Fragment>
                    );
                })
            )}
            <MarkerClusterer
                options={{ imagePath: '/m', imageExtension: 'png' }}
                onLoad={setTenantClusterer}
                onUnmount={() => setTenantClusterer(null)}
            >
                {(clusterer) => (
                    <>
                        {tenants.map((t) => (
                            <Marker
                                key={t.id}
                                position={{ lat: t.lat, lng: t.lng }}
                                clusterer={clusterer}
                                noClustererRedraw
                                onClick={() => toggleShowInfoWindow(t.id)}
                            />
                        ))}
                        {selectedTenant && (
                            <InfoWindow
                                position={{ lat: selectedTenant.lat, lng: selectedTenant.lng }}
                                onCloseClick={() => setSelectedTenantId(null)}
                            >
                                {renderInfoContent(selectedTenant)}
                            </InfoWindow>
                        )}
                    </>
                )}
            </MarkerClusterer>
        </GoogleMap>
    );
}
